<template>
  <section class="section section-drama">
    <div class="tech-bg">
      <div class="grid-overlay"></div>
      <div class="floating-particles">
        <div class="particle" v-for="n in 30" :key="n" :style="particleStyle()"></div>
      </div>
      <div class="glow-orb glow-orb-1"></div>
      <div class="glow-orb glow-orb-2"></div>
      <div class="glow-orb glow-orb-3"></div>
      <div class="hex-grid"></div>
    </div>
    <div class="container">
      <div class="card">
        <!-- 标签页 -->
        <div class="tab-container">
          <button 
            class="tab-btn" 
            :class="{ active: currentTab === 'text' }"
            @click="currentTab = 'text'"
          >
            🎨 文生图
          </button>
          <button 
            class="tab-btn" 
            :class="{ active: currentTab === 'image' }"
            @click="currentTab = 'image'"
          >
            🖼️ 图生图
          </button>
        </div>
        <!-- 文生图表单 -->
        <div v-if="currentTab === 'text'" class="tab-content">
          <div class="form-item">
            <label>提示词</label>
            <textarea
              v-model="textForm.prompt"
              :rows="4"
              placeholder="请输入详细的图片描述"
              class="textarea"
            ></textarea>
          </div>
          <div class="form-row">
            <div class="form-item half">
              <label>模型选择</label>
              <div class="model-select-container">
                <div class="selected-model" @click="toggleModelDropdown('text')">
                  <img v-if="textForm.model && models[textForm.model]?.image_url" :src="models[textForm.model]?.image_url" :alt="models[textForm.model]?.name" class="model-icon">
                  <span v-if="textForm.model && models[textForm.model]">{{ models[textForm.model]?.name }}</span>
                  <span v-else>选择模型</span>
                  <span class="dropdown-arrow">▼</span>
                </div>
                <div v-if="modelDropdown === 'text'" class="model-dropdown">
                  <div class="model-group">
                    <div class="model-group-title">🌟 免费模型</div>
                    <div v-for="id in freeModels" :key="id" class="model-item" @click="selectModel('text', id)">
                      <img :src="models[id]?.image_url" :alt="models[id]?.name" class="model-icon">
                      <span class="model-name">{{ models[id]?.name }}</span>
                      <div class="model-tags">
                        <span v-if="models[id]?.is_new" class="model-tag new">NEW</span>
                        <span v-if="models[id]?.is_hot" class="model-tag hot">HOT</span>
                        <span v-if="models[id]?.discount > 0" class="model-tag discount">{{ models[id]?.discount }}% OFF</span>
                      </div>
                    </div>
                  </div>
                  <div class="model-group">
                    <div class="model-group-title">💎 付费模型</div>
                    <div v-for="id in paidModels" :key="id" class="model-item" @click="selectModel('text', id)">
                      <img :src="models[id]?.image_url" :alt="models[id]?.name" class="model-icon">
                      <span class="model-name">{{ models[id]?.name }}</span>
                      <div class="model-tags">
                        <span v-if="models[id]?.is_new" class="model-tag new">NEW</span>
                        <span v-if="models[id]?.is_hot" class="model-tag hot">HOT</span>
                        <span v-if="models[id]?.discount > 0" class="model-tag discount">{{ models[id]?.discount }}% OFF</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="model-hint" v-if="textForm.model && models[textForm.model]">
                <span :class="['model-type', models[textForm.model].type]">
                  {{ models[textForm.model].type === 'free' ? '免费' : '付费' }}
                </span>
                <span class="model-desc">{{ models[textForm.model].description }}</span>
              </div>
            </div>
            <div class="form-item half">
              <label>图片尺寸</label>
              <select v-model="textForm.size" class="select">
                <option value="512x512">512x512 (1:1)</option>
                <option value="1024x1024">1024x1024 (1:1)</option>
                <option value="1024x1792">1024x1792 (9:16)</option>
                <option value="1792x1024">1792x1024 (16:9)</option>
              </select>
            </div>
          </div>

          <div class="generate-area">
            <button class="btn primary" @click="generateTextToImage" :disabled="loading || !isTextToImageValid" :title="isTextToImageValid ? '' : '当前模型不支持文生图功能，请选择其他模型'">
              {{ loading ? '生成中...' : '生成图片' }}
            </button>
            <div v-if="loading" class="progress-container">
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: progress + '%' }">
                  <div class="progress-wave"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 图生图表单 -->
        <div v-if="currentTab === 'image'" class="tab-content">
          <div class="form-item">
            <label>上传参考图片</label>
            <div class="upload-area" @click="triggerUpload">
              <div v-if="!imageForm.image" class="upload-placeholder">
                <div class="upload-icon">📷</div>
                <div class="upload-text">点击或拖拽上传图片</div>
                <div class="upload-hint">支持 JPG、PNG 格式</div>
              </div>
              <img v-else :src="imageForm.imagePreview" class="upload-preview" alt="预览">
              <button v-if="imageForm.image" class="remove-image-btn" @click.stop="removeImage">×</button>
            </div>
            <input 
              type="file" 
              ref="fileInput" 
              accept="image/*" 
              @change="handleImageUpload" 
              style="display: none"
            >
          </div>
          <div class="form-item">
            <label>提示词</label>
            <textarea
              v-model="imageForm.prompt"
              :rows="3"
              placeholder="请输入你想要修改的描述"
              class="textarea"
            ></textarea>
          </div>
          <div class="form-row">
            <div class="form-item half">
              <label>模型选择</label>
              <div class="model-select-container">
                <div class="selected-model" @click="toggleModelDropdown('image')">
                  <img v-if="imageForm.model && models[imageForm.model]?.image_url" :src="models[imageForm.model]?.image_url" :alt="models[imageForm.model]?.name" class="model-icon">
                  <span v-if="imageForm.model && models[imageForm.model]">{{ models[imageForm.model]?.name }}</span>
                  <span v-else>选择模型</span>
                  <span class="dropdown-arrow">▼</span>
                </div>
                <div v-if="modelDropdown === 'image'" class="model-dropdown">
                  <div class="model-group">
                    <div class="model-group-title">🌟 免费模型</div>
                    <div v-for="id in freeModels" :key="id" class="model-item" @click="selectModel('image', id)">
                      <img :src="models[id]?.image_url" :alt="models[id]?.name" class="model-icon">
                      <span class="model-name">{{ models[id]?.name }}</span>
                      <div class="model-tags">
                        <span v-if="models[id]?.is_new" class="model-tag new">NEW</span>
                        <span v-if="models[id]?.is_hot" class="model-tag hot">HOT</span>
                        <span v-if="models[id]?.discount > 0" class="model-tag discount">{{ models[id]?.discount }}% OFF</span>
                      </div>
                    </div>
                  </div>
                  <div class="model-group">
                    <div class="model-group-title">💎 付费模型</div>
                    <div v-for="id in paidModels" :key="id" class="model-item" @click="selectModel('image', id)">
                      <img :src="models[id]?.image_url" :alt="models[id]?.name" class="model-icon">
                      <span class="model-name">{{ models[id]?.name }}</span>
                      <div class="model-tags">
                        <span v-if="models[id]?.is_new" class="model-tag new">NEW</span>
                        <span v-if="models[id]?.is_hot" class="model-tag hot">HOT</span>
                        <span v-if="models[id]?.discount > 0" class="model-tag discount">{{ models[id]?.discount }}% OFF</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="model-hint" v-if="imageForm.model && models[imageForm.model]">
                <span :class="['model-type', models[imageForm.model].type]">
                  {{ models[imageForm.model].type === 'free' ? '免费' : '付费' }}
                </span>
                <span class="model-desc">{{ models[imageForm.model].description }}</span>
              </div>
            </div>
            <div class="form-item half">
              <label>相似度 (0-1)</label>
              <input 
                type="range" 
                v-model="imageForm.strength" 
                min="0.1" 
                max="0.9" 
                step="0.1"
                class="slider"
              >
              <div class="slider-value">{{ imageForm.strength }}</div>
              <div class="slider-hint">值越大越接近原图，值越小变化越大</div>
            </div>
          </div>
          <div class="form-item">
            <label>图片尺寸</label>
            <select v-model="imageForm.size" class="select">
              <option value="512x512">512x512 (1:1)</option>
              <option value="1024x1024">1024x1024 (1:1)</option>
              <option value="1024x1792">1024x1792 (9:16)</option>
              <option value="1792x1024">1792x1024 (16:9)</option>
            </select>
          </div>
          <div class="generate-area">
            <button class="btn primary" @click="generateImageToImage" :disabled="loading || !imageForm.image || !imageForm.prompt.trim() || !isImageToImageValid" :title="isImageToImageValid ? '' : '当前模型不支持图生图功能，请选择其他模型'">
              {{ loading ? '生成中...' : '生成图片' }}
            </button>
            <div v-if="loading" class="progress-container">
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: progress + '%' }">
                  <div class="progress-wave"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div v-if="result" class="result-section">
          <div class="result-header">
            <h4>生成结果</h4>
            <button 
              class="refresh-btn" 
              @click="refreshImages" 
              :disabled="refreshCooldown > 0"
              :title="refreshCooldown > 0 ? `请等待 ${refreshCooldown} 秒` : '重新生成'"
            >
              <span v-if="refreshCooldown > 0">{{ refreshCooldown }}s</span>
              <span v-else>🔄</span>
            </button>
          </div>
          <div :class="result.images.length === 1 ? 'result-single' : 'result-grid'">
            <div class="result-item" v-for="(image, index) in result.images" :key="index">
              <img 
                :src="isBase64(image) ? 'data:image/png;base64,' + image : image" 
                :alt="'生成的图片 '+ (index+1)" 
                class="result-image"
                @load="onImageLoad(index)"
                @error="onImageError(index)"
              >
            </div>
          </div>
        </div>

        <AdBanner position="image-generate" />
      </div>
    </div>
  </section>
</template>

<script>
import axios from 'axios'
import { authMixin } from '../utils/authMixin'
import appCache from '../utils/appCache'
import AdBanner from '../components/AdBanner.vue'

export default {
  name: 'ImageGenerator',
  components: {
    AdBanner
  },
  mixins: [authMixin],
  data() {
    return {
      currentTab: 'text',
      textForm: {
        prompt: '',
        model: '',
        size: '1024x1024'
      },
      imageForm: {
        image: null,
        imagePreview: null,
        prompt: '',
        model: '',
        size: '1024x1024',
        strength: 0.5
      },
      loading: false,
      progress: 0,
      progressInterval: null,
      result: null,
      loadedImages: [],
      refreshCooldown: 0,
      refreshTimer: null,
      lastUsedForm: null,
      history: [],
      models: {},
      freeModels: [],
      paidModels: [],
      modelDropdown: null,
      user_id: 0
    }
  },
  mounted() {
    const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
    this.user_id = userInfo.id || 0
    this.loadHistory()
    this.loadModels()
    
    // 处理URL查询参数，自动触发生成
    this.handleQueryParams()
  },
  computed: {
    // 检查文生图当前模型是否支持
    isTextToImageValid() {
      if (!this.textForm.model) return false
      const model = this.models[this.textForm.model]
      console.log('[DEBUG] isTextToImageValid - model:', model)
      console.log('[DEBUG] isTextToImageValid - capabilities:', model?.capabilities)
      if (!model || !model.capabilities) {
        console.log('[DEBUG] isTextToImageValid - 返回 true（模型或capabilities不存在）')
        return true
      }
      const result = model.capabilities.includes('text_to_image')
      console.log('[DEBUG] isTextToImageValid - 是否支持:', result)
      return result
    },
    // 检查图生图当前模型是否支持
    isImageToImageValid() {
      if (!this.imageForm.model) return false
      const model = this.models[this.imageForm.model]
      console.log('[DEBUG] isImageToImageValid - model:', model)
      console.log('[DEBUG] isImageToImageValid - capabilities:', model?.capabilities)
      if (!model || !model.capabilities) {
        console.log('[DEBUG] isImageToImageValid - 返回 true（模型或capabilities不存在）')
        return true
      }
      const result = model.capabilities.includes('image_to_image')
      console.log('[DEBUG] isImageToImageValid - 是否支持:', result)
      return result
    }
  },
  methods: {
    particleStyle() {
      const size = Math.random() * 4 + 1
      const left = Math.random() * 100
      const top = Math.random() * 100
      const duration = Math.random() * 20 + 10
      const delay = Math.random() * 10
      return {
        width: `${size}px`,
        height: `${size}px`,
        left: `${left}%`,
        top: `${top}%`,
        animationDuration: `${duration}s`,
        animationDelay: `${delay}s`
      }
    },
    async handleQueryParams() {
      const query = this.$route.query
      if (!query.type || !query.prompt) return
      
      if (query.type === 'text-to-image') {
        this.textForm.prompt = query.prompt
        this.textForm.model = query.model || 'stable-diffusion-v1-5'
        this.textForm.size = query.size || '512x512'
      } else if (query.type === 'image-to-image') {
        this.imageForm.prompt = query.prompt
        this.imageForm.model = query.model || 'stable-diffusion-v1-5'
        this.imageForm.size = query.size || '512x512'
        this.imageForm.strength = query.strength || 0.7
      }
      
      this.$router.replace({ query: {} })
    },
    startProgress() {
      this.progress = 0
      this.loadedImages = []
      this.progressInterval = setInterval(() => {
        if (this.progress < 90) {
          this.progress += Math.random() * 5 + 2.5
          if (this.progress > 90) this.progress = 90
        }
      }, 500)
    },
    stopProgress() {
      if (this.progressInterval) {
        clearInterval(this.progressInterval)
        this.progressInterval = null
      }
      this.progress = 100
      this.checkAllImagesLoaded()
    },
    onImageLoad(index) {
      if (!this.loadedImages.includes(index)) {
        this.loadedImages.push(index)
        this.checkAllImagesLoaded()
      }
    },
    onImageError(index) {
      if (!this.loadedImages.includes(index)) {
        this.loadedImages.push(index)
        this.checkAllImagesLoaded()
      }
    },
    checkAllImagesLoaded() {
      if (this.result && this.loadedImages.length >= this.result.images.length) {
        setTimeout(() => {
          this.loading = false
          this.progress = 0
        }, 300)
      }
    },
    async generateTextToImage() {
      if (!this.textForm.prompt) {
        alert('请输入提示词')
        return
      }
      
      const model = this.models[this.textForm.model]
      if (model && model.capabilities && !model.capabilities.includes('text_to_image')) {
        alert('该模型不支持文生图功能，请更换模型')
        return
      }
      
      this.$router.push({
        path: '/image-generator',
        query: {
          prompt: this.textForm.prompt,
          model: this.textForm.model,
          size: this.textForm.size,
          type: 'text-to-image'
        }
      })
    },
    async generateImageToImage() {
      if (!this.imageForm.image) {
        alert('请先上传参考图片')
        return
      }
      if (!this.imageForm.prompt.trim()) {
        alert('请输入提示词')
        return
      }
      
      const model = this.models[this.imageForm.model]
      if (model && model.capabilities && !model.capabilities.includes('image_to_image')) {
        alert('该模型不支持图生图功能，请更换模型')
        return
      }
      
      // 先上传参考图片到存储，避免base64数据通过URL参数传递被截断
      this.loading = true
      try {
        const formData = new FormData()
        formData.append('image', this.imageForm.image)
        formData.append('user_id', this.user_id)
        
        const uploadResponse = await axios.post('/upload-image', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        })
        
        const imageUrl = uploadResponse.data.url
        
        this.$router.push({
          path: '/image-generator',
          query: {
            prompt: this.imageForm.prompt,
            model: this.imageForm.model,
            size: this.imageForm.size,
            strength: this.imageForm.strength,
            type: 'image-to-image',
            referenceImage: imageUrl
          }
        })
      } catch (error) {
        console.error('上传参考图片失败:', error)
        alert('上传参考图片失败，请重试')
        this.loading = false
      }
    },
    async refreshImages() {
      if (this.refreshCooldown > 0) return
      if (!this.lastUsedForm) return
      
      this.startRefreshCooldown()
      const randomSuffix = ` #${Date.now()}`
      
      if (this.lastUsedForm.type === 'text') {
        const originalPrompt = this.textForm.prompt
        this.textForm.prompt += randomSuffix
        await this.generateTextToImage()
        this.textForm.prompt = originalPrompt
      } else {
        const originalPrompt = this.imageForm.prompt
        this.imageForm.prompt += randomSuffix
        await this.generateImageToImage()
        this.imageForm.prompt = originalPrompt
      }
    },
    startRefreshCooldown() {
      this.refreshCooldown = 10
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer)
      }
      this.refreshTimer = setInterval(() => {
        this.refreshCooldown--
        if (this.refreshCooldown <= 0) {
          clearInterval(this.refreshTimer)
          this.refreshTimer = null
        }
      }, 1000)
    },
    triggerUpload() {
      this.$refs.fileInput.click()
    },
    handleImageUpload(event) {
      const file = event.target.files[0]
      if (!file) return
      if (!file.type.startsWith('image/')) {
        alert('请选择图片文件')
        return
      }
      if (file.size > 10 * 1024 * 1024) {
        alert('图片大小不能超过10MB')
        return
      }
      this.imageForm.image = file
      const reader = new FileReader()
      reader.onload = (e) => {
        this.imageForm.imagePreview = e.target.result
      }
      reader.readAsDataURL(file)
    },
    removeImage() {
      this.imageForm.image = null
      this.imageForm.imagePreview = null
    },
    async loadHistory() {
      try {
        const response = await axios.get('/api/image-history', {
          params: { user_id: this.user_id }
        })
        this.history = response.data
      } catch (error) {
        console.error('加载历史记录失败', error)
      }
    },
    async loadModels() {
      try {
        console.log('[DEBUG loadModels] 开始加载模型...')
        const textToImageModels = await appCache.getModelsByCategory('image', false, 'text_to_image')
        const imageToImageModels = await appCache.getModelsByCategory('image', false, 'image_to_image')
        
        console.log('[DEBUG loadModels] textToImageModels:', textToImageModels.map(m => m.identifier))
        console.log('[DEBUG loadModels] imageToImageModels:', imageToImageModels.map(m => m.identifier))
        console.log('[DEBUG loadModels] wanx2.1-imageedit 数据:', imageToImageModels.find(m => m.identifier === 'wanx2.1-imageedit'))
        
        this.models = {}
        this.freeModels = []
        this.paidModels = []
        this.textToImageModels = []
        this.imageToImageModels = []
        
        textToImageModels.forEach(model => {
          this.models[model.identifier] = {
            name: model.name,
            description: model.description,
            type: model.model_type,
            image_url: model.image_url,
            is_new: model.is_new,
            is_hot: model.is_hot,
            discount: model.discount,
            capabilities: model.capabilities || []
          }
          this.textToImageModels.push(model.identifier)
          if (model.model_type === 'free') {
            this.freeModels.push(model.identifier)
          } else {
            this.paidModels.push(model.identifier)
          }
        })
        
        imageToImageModels.forEach(model => {
          const identifier = model.identifier
          if (!this.models[identifier]) {
            this.models[identifier] = {
              name: model.name,
              description: model.description,
              type: model.model_type,
              image_url: model.image_url,
              is_new: model.is_new,
              is_hot: model.is_hot,
              discount: model.discount,
              capabilities: model.capabilities || []
            }
          } else {
            // 模型已存在，合并 capabilities
            const existingCaps = this.models[identifier].capabilities || []
            const newCaps = model.capabilities || []
            this.models[identifier].capabilities = [...new Set([...existingCaps, ...newCaps])]
          }
          this.imageToImageModels.push(identifier)
          if (model.model_type === 'free' && !this.freeModels.includes(identifier)) {
            this.freeModels.push(identifier)
          } else if (model.model_type === 'paid' && !this.paidModels.includes(identifier)) {
            this.paidModels.push(identifier)
          }
        })
        
        console.log('[DEBUG loadModels] 最终 models:', this.models)
        console.log('[DEBUG loadModels] wanx2.1-imageedit 最终数据:', this.models['wanx2.1-imageedit'])
        
        try {
          const response = await axios.get('/admin/config/image')
          if (response.data.code === 200) {
            const config = response.data.data
            const defaultModel = config.model || ''
            const defaultSize = config.defaultImageSize || '1024x1024 (1:1)'
            
            if (defaultModel && this.textToImageModels.includes(defaultModel)) {
              this.textForm.model = defaultModel
              this.imageForm.model = defaultModel
            } else if (this.textToImageModels.length > 0) {
              this.textForm.model = this.freeModels.find(m => this.textToImageModels.includes(m)) || this.textToImageModels[0]
              this.imageForm.model = this.freeModels.find(m => this.imageToImageModels.includes(m)) || this.imageToImageModels[0]
            }
            
            if (defaultSize) {
              const sizeValue = defaultSize.split(' (')[0]
              this.textForm.size = sizeValue
              this.imageForm.size = sizeValue
            }
          }
        } catch (error) {
          console.error('获取图片配置失败:', error)
        }
      } catch (error) {
        console.error('加载模型列表失败', error)
      }
    },
    toggleModelDropdown(type) {
      if (this.modelDropdown === type) {
        this.modelDropdown = null
      } else {
        this.modelDropdown = type
      }
    },
    selectModel(type, modelId) {
      if (type === 'text') {
        this.textForm.model = modelId
      } else if (type === 'image') {
        this.imageForm.model = modelId
      }
      this.modelDropdown = null
    },
    isBase64(str) {
      return str && /^[A-Za-z0-9+/=]+$/.test(str)
    }
  },
  beforeDestroy() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval)
    }
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer)
    }
  }
}
</script>

<style scoped>
.section-drama {
  padding-top: 100px;
  overflow-y: auto;
  min-height: 100vh;
  background: linear-gradient(180deg, #0a0e1a 0%, #0d1224 50%, #0a0e1a 100%);
  position: relative;
}

.tech-bg {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}

.grid-overlay {
  position: absolute;
  width: 100%;
  height: 100%;
  background-image: 
    linear-gradient(rgba(0, 230, 172, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 230, 172, 0.03) 1px, transparent 1px);
  background-size: 50px 50px;
  animation: gridMove 20s linear infinite;
}

@keyframes gridMove {
  0% { transform: translate(0, 0); }
  100% { transform: translate(50px, 50px); }
}

.floating-particles {
  position: absolute;
  width: 100%;
  height: 100%;
}

.particle {
  position: absolute;
  background: rgba(0, 230, 172, 0.4);
  border-radius: 50%;
  animation: floatParticle linear infinite;
  filter: blur(1px);
  box-shadow: 0 0 8px rgba(0, 230, 172, 0.3);
}

@keyframes floatParticle {
  0% { transform: translateY(0) translateX(0); opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { transform: translateY(-100vh) translateX(50px); opacity: 0; }
}

.glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(60px);
  opacity: 0.15;
}

.glow-orb-1 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(0, 204, 153, 0.6) 0%, transparent 70%);
  top: 20%;
  left: 10%;
  animation: floatOrb1 15s ease-in-out infinite;
}

.glow-orb-2 {
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, rgba(0, 230, 172, 0.6) 0%, transparent 70%);
  top: 50%;
  right: 15%;
  animation: floatOrb2 18s ease-in-out infinite;
}

.glow-orb-3 {
  width: 350px;
  height: 350px;
  background: radial-gradient(circle, rgba(79, 172, 254, 0.6) 0%, transparent 70%);
  bottom: 10%;
  left: 50%;
  animation: floatOrb3 20s ease-in-out infinite;
}

@keyframes floatOrb1 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(100px, -50px); }
}

@keyframes floatOrb2 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-80px, 80px); }
}

@keyframes floatOrb3 {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(-60px, -100px); }
}

.hex-grid {
  position: absolute;
  width: 100%;
  height: 100%;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='28' height='49' viewBox='0 0 28 49'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%2300e6ac' fill-opacity='0.02'%3E%3Cpath d='M13.99 9.25l13 7.5v15l-13 7.5L1 31.75v-15l12.99-7.5zM3 17.9v12.7l10.99 6.34 11-6.35V17.9l-11-6.34L3 17.9zM0 15l12.98-7.5V0h-2v6.35L0 12.69v2.3zm0 18.5L12.98 41v8h-2v-6.85L0 35.81v-2.3zM15 0v7.5L27.99 15H28v-2.31h-.01L17 6.35V0h-2zm0 49v-8l12.99-7.5H28v2.31h-.01L17 42.15V49h-2z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.4;
}

.container {
  position: relative;
  z-index: 1;
}

.page-head {
  text-align: center;
  margin-bottom: 40px;
}

.page-head h2 {
  font-size: 32px;
  margin-bottom: 10px;
}

.page-head p {
  color: #888;
  font-size: 16px;
}

/* 标签页样式 */
.tab-container {
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  padding-bottom: 10px;
}

.tab-btn {
  padding: 12px 24px;
  background: transparent;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  color: #aaa;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
}

.tab-btn:hover {
  background: rgba(255,255,255,0.05);
  color: #fff;
}

.tab-btn.active {
  background: linear-gradient(135deg, #00e6ac, #00cc99);
  border-color: #00e6ac;
  color: #000;
  font-weight: 600;
}

.tab-content {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.card {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 16px;
  padding: 40px;
  max-width: 900px;
  margin: 0 auto;
}

.form-item {
  margin-bottom: 20px;
}

.form-row {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
}

.form-item.half {
  flex: 1;
}

label {
  display: block;
  margin-bottom: 8px;
  color: #aaa;
  font-size: 14px;
}

.textarea {
  width: 100%;
  padding: 12px 16px;
  background: #1a1e2d;
  border: 1px solid #333;
  border-radius: 8px;
  color: #fff;
  outline: none;
  resize: vertical;
  font-family: inherit;
}

.textarea:focus {
  border-color: #00e6ac;
}

.select {
  width: 100%;
  padding: 12px 16px;
  background: #1a1e2d;
  border: 1px solid #333;
  border-radius: 8px;
  color: #fff;
  outline: none;
}

.select:focus {
  border-color: #00e6ac;
}

.input {
  width: 100%;
  padding: 12px 16px;
  background: #1a1e2d;
  border: 1px solid #333;
  border-radius: 8px;
  color: #fff;
  outline: none;
}

.input:focus {
  border-color: #00e6ac;
}

.upload-area {
  width: 100%;
  min-height: 200px;
  background: #1a1e2d;
  border: 2px dashed #333;
  border-radius: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;
}

.upload-area:hover {
  border-color: #00e6ac;
  background: rgba(0, 230, 172, 0.05);
}

.upload-placeholder {
  text-align: center;
  color: #888;
}

.upload-icon {
  font-size: 48px;
  margin-bottom: 10px;
}

.upload-text {
  font-size: 16px;
  margin-bottom: 5px;
}

.upload-hint {
  font-size: 12px;
  color: #666;
}

.upload-preview {
  max-width: 100%;
  max-height: 300px;
  object-fit: contain;
}

.remove-image-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.remove-image-btn:hover {
  background: #ff4757;
  transform: scale(1.1);
}

.upload-area {
  position: relative;
}

.slider {
  width: 100%;
  margin: 10px 0;
  cursor: pointer;
}

.slider-value {
  text-align: center;
  color: #00e6ac;
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 5px;
}

.slider-hint {
  text-align: center;
  color: #666;
  font-size: 12px;
}

.generate-area {
  display: flex;
  gap: 20px;
  align-items: center;
  margin-top: 30px;
}

.progress-container {
  flex: 1;
}

.progress-bar {
  width: 100%;
  height: 14px;
  background: rgba(255,255,255,0.1);
  border-radius: 7px;
  overflow: hidden;
  position: relative;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #00e6ac, #00cc99, #00e6ac);
  border-radius: 7px;
  position: relative;
  transition: width 0.3s ease;
  background-size: 200% 100%;
  animation: gradientMove 2s linear infinite;
}

@keyframes gradientMove {
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 200% 50%;
  }
}

.progress-wave {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    -45deg,
    transparent 33%,
    rgba(255,255,255,0.3) 33%,
    rgba(255,255,255,0.3) 66%,
    transparent 66%
  );
  background-size: 20px 20px;
  animation: waveMove 1s linear infinite;
}

@keyframes waveMove {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 20px 0;
  }
}

.result-section {
  margin-top: 30px;
  padding-top: 30px;
  border-top: 1px solid rgba(255,255,255,0.05);
}

.result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.result-header h4 {
  font-size: 18px;
  margin: 0;
}

.refresh-btn {
  background: rgba(0, 230, 172, 0.1);
  border: 1px solid rgba(0, 230, 172, 0.3);
  color: #00e6ac;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 50px;
}

.refresh-btn:hover:not(:disabled) {
  background: rgba(0, 230, 172, 0.2);
}

.refresh-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.result-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.result-single {
  display: flex;
  justify-content: center;
  align-items: center;
}

.result-item {
  display: flex;
  justify-content: center;
  align-items: center;
}

.result-image {
  width: 100%;
  height: auto;
  max-height: 600px;
  object-fit: contain;
  border-radius: 8px;
}

@media (max-width: 768px) {
  .form-row {
    flex-direction: column;
  }
  .card {
    padding: 20px;
  }
  .tabs {
    flex-direction: column;
  }
  .tab {
    width: 100%;
  }
  .generate-area {
    flex-direction: column;
    align-items: stretch;
  }
  .progress-container {
    width: 100%;
  }
}

.model-hint {
  margin-top: 8px;
  font-size: 12px;
  color: #888;
}

.model-type {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  margin-right: 8px;
}

.model-type.free {
  background: rgba(0, 230, 172, 0.1);
  color: #00e6ac;
}

.model-type.paid {
  background: rgba(255, 152, 0, 0.1);
  color: #ff9800;
}

.model-desc {
  color: #666;
}

/* 模型标签样式 */
.model-tag {
  display: inline-block;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  margin-left: 4px;
  vertical-align: middle;
}

.model-tag.new {
  background: linear-gradient(135deg, #00e6ac, #00cc99);
  color: #000;
}

.model-tag.hot {
  background: linear-gradient(135deg, #ff6b6b, #ee5a6f);
  color: #fff;
}

.model-tag.discount {
  background: linear-gradient(135deg, #ff9800, #f57c00);
  color: #fff;
}

/* 模型选择下拉菜单样式 */
.model-select-container {
  position: relative;
  width: 100%;
}

.selected-model {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.selected-model:hover {
  border-color: #00e6ac;
  background: rgba(0, 230, 172, 0.05);
}

.model-icon {
  width: 24px;
  height: 24px;
  margin-right: 10px;
  border-radius: 4px;
}

.dropdown-arrow {
  margin-left: auto;
  font-size: 12px;
  color: #999;
  transition: transform 0.3s ease;
}

.model-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: #1a1e2d;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  margin-top: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  max-height: 300px;
  overflow-y: auto;
}

.model-group {
  padding: 10px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.model-group:last-child {
  border-bottom: none;
}

.model-group-title {
  padding: 0 15px 10px;
  font-size: 14px;
  font-weight: 600;
  color: #00e6ac;
}

.model-item {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.model-item:hover {
  background: rgba(0, 230, 172, 0.1);
}

.model-item .model-name {
  flex: 1;
  margin-right: 10px;
}

.model-tags {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

/* 按钮样式 */
.btn {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn.primary {
  background: linear-gradient(135deg, #00e6ac, #00cc99);
  color: #000;
}

.btn.primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 230, 172, 0.3);
}
</style>
